import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { fetchDescargarPedidoPdf, fetchMisPedidosCliente, PedidoConDetalleDto } from "../services/pedidoService";
import { ClienteCuentaCorrienteDto, fetchGetMiCuentaCorriente } from "../services/clienteService";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";

const formatDate = (value: string) => {
  const date = new Date(String(value).includes("T") ? value : `${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("es-AR");
};

const field = (row: any, ...keys: string[]) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return "";
};

const moneyText = (value: string | number | undefined) => {
  const raw = String(value ?? "").trim();
  if (!raw) return CurrencyFormatter.format(0);
  const normalized = Number(raw.replace(/\$/g, "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(normalized) ? CurrencyFormatter.format(normalized) : raw;
};

const PortalClientes = () => {
  const navigate = useNavigate();
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);
  const [pedidos, setPedidos] = useState<PedidoConDetalleDto[]>([]);
  const [cuentaCorriente, setCuentaCorriente] = useState<ClienteCuentaCorrienteDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCuenta, setLoadingCuenta] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const cargarPedidos = useCallback(() => {
    if (!userLoggedIn) return;
    setLoading(true);
    fetchMisPedidosCliente(userLoggedIn)
      .then(setPedidos)
      .catch((err) => {
        console.log(err);
        showNotification(err?.message ?? "No se pudieron cargar sus pedidos", "error");
      })
      .finally(() => setLoading(false));
  }, [userLoggedIn, showNotification]);

  useEffect(() => {
    cargarPedidos();
  }, [cargarPedidos]);

  const cargarCuentaCorriente = useCallback(() => {
    if (!userLoggedIn) return;
    setLoadingCuenta(true);
    fetchGetMiCuentaCorriente(userLoggedIn)
      .then(setCuentaCorriente)
      .catch((err) => {
        console.log(err);
        showNotification(err?.message ?? "No se pudo cargar la cuenta corriente", "error");
      })
      .finally(() => setLoadingCuenta(false));
  }, [userLoggedIn, showNotification]);

  useEffect(() => {
    cargarCuentaCorriente();
  }, [cargarCuentaCorriente]);

  const totalPendiente = pedidos
    .filter((pedido) => (pedido.clientePedido.origen ?? "").toUpperCase().includes("WEB"))
    .reduce((acc, pedido) => acc + pedido.clientePedido.total, 0);

  const descargarPdf = (pedido: PedidoConDetalleDto) => {
    if (!userLoggedIn) return;
    setDownloadingId(pedido.clientePedido.idClientePedido);
    fetchDescargarPedidoPdf(userLoggedIn, pedido.clientePedido.idLocal, pedido.clientePedido.idClientePedido)
      .catch((err) => {
        console.log(err);
        showNotification(err?.message ?? "No se pudo descargar el PDF", "error");
      })
      .finally(() => setDownloadingId(null));
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 3 }}>
        <Box>
          <Typography variant="h4">Portal de Clientes</Typography>
          <Typography color="text.secondary">{userLoggedIn?.cliente ?? userLoggedIn?.username}</Typography>
        </Box>
        <Button variant="contained" startIcon={<ShoppingCartIcon />} onClick={() => navigate("/pedidos/carrito")}>
          Nuevo pedido
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography color="text.secondary">Pedidos recientes</Typography>
            <Typography variant="h4">{pedidos.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography color="text.secondary">Importe web reciente</Typography>
            <Typography variant="h4">{CurrencyFormatter.format(totalPendiente)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography color="text.secondary">Saldo cuenta corriente</Typography>
            <Typography variant="h4">{moneyText(cuentaCorriente?.saldo)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={5}>
          <Paper sx={{ borderRadius: 2, overflow: "hidden", height: "100%" }}>
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningAmberIcon color="warning" />
                <Typography variant="h6">Facturas pendientes</Typography>
              </Stack>
              <Chip label={moneyText(cuentaCorriente?.totalDeuda)} color="warning" variant="outlined" />
            </Box>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Deuda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingCuenta ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={18} sx={{ my: 2 }} />
                      </TableCell>
                    </TableRow>
                  ) : !cuentaCorriente?.facturasImpagas?.length ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No hay facturas pendientes.</TableCell>
                    </TableRow>
                  ) : cuentaCorriente.facturasImpagas.map((factura: any) => (
                    <TableRow key={`${field(factura, "id", "ID")}-${field(factura, "idlocal", "IDLOCAL")}`}>
                      <TableCell>{field(factura, "fecha_OPERACION", "fechA_OPERACION", "FECHA_OPERACION")}</TableCell>
                      <TableCell>{field(factura, "tipo_DE_FACTURA", "tipO_DE_FACTURA", "TIPO_DE_FACTURA")} {field(factura, "factura", "FACTURA")}</TableCell>
                      <TableCell align="right">{moneyText(field(factura, "total", "TOTAL"))}</TableCell>
                      <TableCell align="right">{moneyText(field(factura, "deuda", "DEUDA"))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ borderRadius: 2, overflow: "hidden", height: "100%" }}>
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountBalanceWalletIcon color="primary" />
                <Typography variant="h6">Estado de cuenta</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Chip label={`Debe ${moneyText(cuentaCorriente?.totalDebe)}`} size="small" />
                <Chip label={`Haber ${moneyText(cuentaCorriente?.totalHaber)}`} size="small" />
              </Stack>
            </Box>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell align="right">Debe</TableCell>
                    <TableCell align="right">Haber</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingCuenta ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress size={18} sx={{ my: 2 }} />
                      </TableCell>
                    </TableRow>
                  ) : !cuentaCorriente?.estadoCuenta?.length ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No hay movimientos de cuenta corriente.</TableCell>
                    </TableRow>
                  ) : cuentaCorriente.estadoCuenta.map((mov, index) => (
                    <TableRow key={`${mov.fecha}-${mov.hora}-${index}`}>
                      <TableCell>{mov.fecha}</TableCell>
                      <TableCell>{mov.comprobante}</TableCell>
                      <TableCell align="right">{moneyText(mov.debe)}</TableCell>
                      <TableCell align="right">{moneyText(mov.haber)}</TableCell>
                      <TableCell align="right">{moneyText(mov.saldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h6">Mis pedidos</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pedido</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Entrega</TableCell>
                <TableCell>Origen</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ py: 2 }}>
                      <CircularProgress size={18} />
                      <span>Cargando pedidos...</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : pedidos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Todavia no hay pedidos registrados.</TableCell>
                </TableRow>
              ) : pedidos.map((pedido) => (
                <TableRow key={`${pedido.clientePedido.idLocal}-${pedido.clientePedido.idClientePedido}`}>
                  <TableCell>{pedido.clientePedido.idClientePedido}</TableCell>
                  <TableCell>{formatDate(pedido.clientePedido.fecha)}</TableCell>
                  <TableCell>{formatDate(pedido.clientePedido.fechaEntrega)}</TableCell>
                  <TableCell>{pedido.clientePedido.origen || "Mostrador"}</TableCell>
                  <TableCell align="right">{CurrencyFormatter.format(pedido.clientePedido.total)}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={downloadingId === pedido.clientePedido.idClientePedido ? <CircularProgress color="inherit" size={16} /> : <DownloadIcon />}
                      disabled={downloadingId === pedido.clientePedido.idClientePedido}
                      onClick={() => descargarPdf(pedido)}
                    >
                      Descargar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PortalClientes;
